import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, RefreshCw, ArrowUpRight, ArrowDownRight, Zap, MoonStar, Ban, Info } from "lucide-react";
import TopBar from "@/components/TopBar";
import TickerSearchDialog from "@/components/TickerSearchDialog";
import DisclaimerFooter from "@/components/DisclaimerFooter";
import TradeNowDialog from "@/components/TradeNowDialog";
import { ValidationBanner, useValidationStatus } from "@/components/Validation";
import { api } from "@/lib/api";

function fmt(v, d = 2) {
  if (v == null) return "—";
  return Number(v).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}

function formatCloseTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", timeZone: "America/New_York" });
  } catch { return ""; }
}

export default function WhatNow() {
  const nav = useNavigate();
  const validation = useValidationStatus();
  const [open, setOpen] = useState(false);
  const [openTradeNow, setOpenTradeNow] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/intraday/signal/what_now", { params: refresh ? { refresh: true } : {} });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load What Now results.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const isOpen = !!data?.market_is_open;
  const entriesClosed = !!data?.entries_closed;
  const clockUnknown = !!data && (data.session === "UNKNOWN" || data.session == null);
  const clockResolved = !!data && !clockUnknown;

  return (
    <div className="min-h-screen bg-[#080B11] flex flex-col">
      <TopBar onOpenSearch={() => setOpen(true)} />
      <main className="flex-1 pb-14">
        <div className="max-w-[1400px] mx-auto p-3 sm:p-4">

          {/* Hero */}
          <div className="border border-[#1E293B] bg-[#0F1626] px-5 py-4 flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-amber-400" />
                <span className="mono text-[11px] uppercase tracking-widest text-amber-300">Intraday · What Now?</span>
              </div>
              <h1 className="mono text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-100 mt-1" data-testid="whatnow-title">
                What should I trade right now?
              </h1>
              <p className="mono text-[11px] text-slate-500 mt-1" data-testid="whatnow-status-line">
                {!data
                  ? "Loading market status…"
                  : clockUnknown
                    ? "Loading market status… clock source temporarily unavailable."
                    : isOpen
                      ? `Live intraday scan. Universe: ${data?.universe_size || 30} tickers. Cached ${data?.ttl_seconds || 90}s.`
                      : "Market is closed. Showing prior-session top-scored setups (not live signals)."}
              </p>
            </div>
            <button
              data-testid="whatnow-trade-now"
              onClick={() => setOpenTradeNow(true)}
              className="mono text-[11px] uppercase tracking-widest px-3 h-9 border border-amber-500/60 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25 font-bold flex items-center gap-2"
            >
              <Zap className="h-3.5 w-3.5" />
              Trade Now
            </button>
            <button
              data-testid="whatnow-refresh"
              onClick={() => load(true)}
              disabled={loading}
              className="mono text-[11px] uppercase tracking-widest px-3 h-9 border border-cyan-500/60 bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/25 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Scan Now
            </button>
          </div>

          {error && (
            <div data-testid="whatnow-error" className="mt-3 border border-red-500/40 bg-red-500/10 p-3 mono text-[11px] text-red-300">
              {error}
            </div>
          )}

          {/* I9 validation banner — persistent honesty on intraday. */}
          {validation?.intraday && (
            <div className="mt-3">
              <ValidationBanner status={validation.intraday} testid="whatnow-validation-banner" />
            </div>
          )}

          {/* Clock unknown — neutral loading skeleton (no premature "closed" claim) */}
          {clockUnknown && (
            <div data-testid="whatnow-clock-unknown" className="mt-3 border border-slate-700 bg-slate-800/40 p-3 mono text-[11px] text-slate-400 flex items-start gap-2">
              <Loader2 className="h-4 w-4 shrink-0 mt-0.5 animate-spin" />
              <div>
                <div className="font-bold uppercase tracking-widest text-[10px] text-slate-300">Loading market status…</div>
                <div className="text-slate-500 mt-1">Clock source is temporarily unavailable. Waiting for next resolution.</div>
              </div>
            </div>
          )}

          {/* Closed market banner (only when clock is resolved) */}
          {clockResolved && !isOpen && (
            <div data-testid="whatnow-closed-banner" className="mt-3 border border-slate-600 bg-slate-800/50 p-3 mono text-[11px] text-slate-300 flex items-start gap-2">
              <MoonStar className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                <div className="font-bold uppercase tracking-widest text-[10px]">Market closed · {data.session?.replace("_", "-")}</div>
                <div className="text-slate-400 mt-1">
                  {data.premarket_candidates_note || "Live signals resume at next open."}
                  {data.next_open && ` Next open: ${formatCloseTime(data.next_open)} ET.`}
                </div>
              </div>
            </div>
          )}

          {/* No-entry-window banner */}
          {data && entriesClosed && (
            <div data-testid="whatnow-no-entry-window" className="mt-3 border border-amber-500/50 bg-amber-500/10 p-3 mono text-[11px] text-amber-300 flex items-start gap-2">
              <Ban className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                <div className="font-bold uppercase tracking-widest text-[10px]">Entries closed for the day</div>
                <div className="text-amber-200/80 mt-1">
                  &lt; 75 min to close ({data.minutes_to_close?.toFixed?.(0) || "?"} min). Monitor open positions only.
                </div>
              </div>
            </div>
          )}

          {/* No qualifying signals banner */}
          {data && data.no_qualifying_signals && isOpen && !entriesClosed && (
            <div data-testid="whatnow-empty" className="mt-3 border border-[#1E293B] bg-[#0F1626] p-4 mono text-[11px] text-slate-400 flex items-start gap-2">
              <Info className="h-4 w-4 text-cyan-400 shrink-0 mt-0.5" />
              <div>
                <div className="font-bold uppercase tracking-widest text-[10px] text-slate-200">No qualifying intraday signals right now</div>
                <div className="text-slate-500 mt-1">
                  Nothing hits the ≥60 score + ≥15 margin threshold across the universe. Below-threshold candidates are listed as "No trade".
                </div>
              </div>
            </div>
          )}

          {/* Top LONG + SHORT (gated behind loaded data — avoids premature "none" claim) */}
          {data ? (
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
              <Section
                title="Top LONG"
                testid="whatnow-longs"
                accent="emerald"
                rows={data?.top_long || []}
                onRowClick={(t) => nav(`/stock/${t}`)}
                icon={ArrowUpRight}
              />
              <Section
                title="Top SHORT"
                testid="whatnow-shorts"
                accent="red"
                rows={data?.top_short || []}
                onRowClick={(t) => nav(`/stock/${t}`)}
                icon={ArrowDownRight}
              />
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="whatnow-loading-skeleton">
              <SectionSkeleton title="Top LONG" accent="emerald" />
              <SectionSkeleton title="Top SHORT" accent="red" />
            </div>
          )}

          {/* No-trade (below-threshold) fallback */}
          {data?.top_ranked_wait?.length > 0 && (
            <div className="mt-4">
              <Section
                title="No Trade · Below Threshold"
                testid="whatnow-noTrade"
                accent="slate"
                rows={data.top_ranked_wait}
                onRowClick={(t) => nav(`/stock/${t}`)}
                isNoTrade
              />
            </div>
          )}

          {data && (
            <div className="mt-3 mono text-[10px] uppercase tracking-widest text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
              <span>Universe: {data.universe_size} · Cache: {data.cache || "?"}</span>
              {data.computed_at && <span>As-of: {new Date(data.computed_at).toLocaleTimeString()}</span>}
              {isOpen && data.minutes_to_close != null && <span>{Math.floor(data.minutes_to_close)} min to close</span>}
            </div>
          )}
        </div>
      </main>
      <TickerSearchDialog open={open} onOpenChange={setOpen} />
      <TradeNowDialog open={openTradeNow} onOpenChange={setOpenTradeNow} />
      <DisclaimerFooter />
    </div>
  );
}

function SectionSkeleton({ title, accent }) {
  const accentCls =
    accent === "emerald" ? "border-emerald-500/30 text-emerald-300/60"
    : accent === "red" ? "border-red-500/30 text-red-300/60"
    : "border-slate-700 text-slate-500";
  return (
    <div className={`border ${accentCls.split(" ")[0]} bg-[#0F1626]`} data-testid={`whatnow-skeleton-${accent}`}>
      <div className={`flex items-center gap-2 border-b border-[#1E293B] px-3 py-2 mono text-[11px] uppercase tracking-widest font-bold ${accentCls.split(" ")[1] || "text-slate-500"}`}>
        {title}
        <span className="ml-auto text-slate-600">—</span>
      </div>
      <div className="p-4 mono text-[11px] text-slate-500 text-center flex items-center justify-center gap-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading candidates…
      </div>
    </div>
  );
}

function Section({ title, testid, accent, rows, onRowClick, isNoTrade, icon: Icon }) {
  const accentCls =
    accent === "emerald" ? "border-emerald-500/50 text-emerald-300"
    : accent === "red" ? "border-red-500/50 text-red-300"
    : "border-slate-600 text-slate-400";
  return (
    <div className={`border ${accentCls.split(" ")[0]} bg-[#0F1626]`} data-testid={testid}>
      <div className={`flex items-center gap-2 border-b border-[#1E293B] px-3 py-2 mono text-[11px] uppercase tracking-widest font-bold ${accentCls.split(" ")[1] || "text-slate-300"}`}>
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {title}
        <span className="ml-auto text-slate-500">{rows?.length || 0}</span>
      </div>
      {(!rows || rows.length === 0) ? (
        <div className="p-4 mono text-[11px] text-slate-500 text-center" data-testid={`${testid}-empty`}>
          None right now.
        </div>
      ) : (
        <div className="divide-y divide-[#1E293B]">
          {rows.map((r) => (
            <button
              key={r.ticker + (r.as_of_ts || "")}
              onClick={() => onRowClick(r.ticker)}
              data-testid={`${testid}-row-${r.ticker}`}
              className="w-full text-left px-3 py-2.5 hover:bg-[#172036] transition-colors flex flex-wrap items-center gap-x-3 gap-y-1"
            >
              <span className="mono text-sm font-bold text-slate-100 min-w-[56px]">{r.ticker}</span>
              <span className={`mono text-[10px] px-1.5 py-0.5 border uppercase tracking-widest ${
                r.signal === "LONG" ? "border-emerald-500/50 text-emerald-300"
                : r.signal === "SHORT" ? "border-red-500/50 text-red-300"
                : "border-slate-600 text-slate-400"
              }`}>
                {isNoTrade ? "No Trade" : r.signal}
              </span>
              <span className="mono text-[11px] tabular-nums text-slate-400">Score {fmt(Math.max(r.long_score || 0, r.short_score || 0), 1)}</span>
              <span className="mono text-[11px] tabular-nums text-slate-400">${fmt(r.price)}</span>
              {r.entry_zone && (
                <span className="mono text-[11px] tabular-nums text-slate-500">
                  Zone {fmt(r.entry_zone.low)}–{fmt(r.entry_zone.high)}
                </span>
              )}
              {r.stop_loss != null && <span className="mono text-[11px] tabular-nums text-red-300/70">Stop {fmt(r.stop_loss)}</span>}
              {r.t1 != null && <span className="mono text-[11px] tabular-nums text-slate-500">T1 {fmt(r.t1)}</span>}
              {r.risk_reward_t1 != null && <span className="mono text-[11px] tabular-nums text-slate-500">R/R {fmt(r.risk_reward_t1, 2)}</span>}
              {r.top_factor && <span className="mono text-[10px] text-slate-500 uppercase tracking-widest ml-auto">{r.top_factor}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
